"use client";

import { useState } from "react";
import { Share2, Check, Copy } from "lucide-react";

interface ShareButtonProps {
  readonly text: string;
  readonly canvasDataUrl?: string;
  readonly className?: string;
  readonly url?: string;
  readonly compact?: boolean;
}

export default function ShareButton({ text, canvasDataUrl, className = "", url, compact = false }: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (typeof window === "undefined") return;
    setSharing(true);

    try {
      // 1. Try to use Web Share API
      if (navigator.share) {
        const shareData: ShareData = {
          title: "FinanceLives Daily Challenge",
          text: text,
          url: url || window.location.href,
        };

        // If canvas image is available and supported, share it as a file
        if (canvasDataUrl && navigator.canShare) {
          try {
            const res = await fetch(canvasDataUrl);
            const blob = await res.blob();
            const file = new File([blob], "financelives-daily-challenge.png", { type: "image/png" });
            
            if (navigator.canShare({ files: [file] })) {
              shareData.files = [file];
            }
          } catch (fileErr) {
            // Fallback to text only sharing if file conversion fails
          }
        }

        await navigator.share(shareData);
        setSharing(false);
        return;
      }
    } catch (shareError) {
      // User cancelled or share failed, fallback to copy to clipboard
    }

    // 2. Fallback to copy to clipboard
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      // Copy failed
    }

    setSharing(false);
  };

  return (
    <button
      onClick={handleShare}
      disabled={sharing}
      className={`btn btn-primary ${className}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? "5px" : "8px",
        padding: compact ? "0.35rem 0.85rem" : "0.625rem 1.25rem",
        fontSize: compact ? "0.8rem" : "0.95rem",
        borderRadius: "var(--radius-full)",
        border: "none",
        fontWeight: "var(--fw-bold)",
        cursor: "pointer",
        transition: "all var(--transition-fast)",
        backgroundColor: "var(--primary)",
        color: "var(--text-on-accent)"
      }}
      aria-label="Share your daily challenge results"
    >
      {copied ? (
        <>
          <Check size={compact ? 14 : 16} />
          <span>Copied!</span>
        </>
      ) : (
        <>
          <Share2 size={compact ? 14 : 16} />
          <span>{sharing ? "Sharing..." : "Share Results"}</span>
        </>
      )}
    </button>
  );
}
